import { useMemo, useState } from 'react';
import { FileDown } from 'lucide-react';
import {
  calcularFaturamento,
  fmtBrl,
  hojeISO,
  inicioFimMesISO,
  inicioFimSemanaISO,
  metodoLabel,
  somarDespesas,
} from '../../utils/barbeariaLogica';
import { baixarResumoMensalPdf } from '../../utils/barbeariaResumoPdf';
import type { BarbeariaEstado, MetodoPagamento } from '../../utils/barbeariaTipos';

interface Props {
  estado: BarbeariaEstado;
}

type Periodo = 'dia' | 'semana' | 'mes';

function faixa(periodo: Periodo): { ini: string; fim: string; label: string } {
  const hoje = hojeISO();
  if (periodo === 'dia') return { ini: hoje, fim: hoje, label: 'Hoje' };
  if (periodo === 'semana') {
    const s = inicioFimSemanaISO();
    return { ...s, label: 'Esta semana' };
  }
  const m = inicioFimMesISO();
  return { ...m, label: 'Este mês' };
}

export default function BarbeariaFinanceiro({ estado }: Props) {
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [avisoPdf, setAvisoPdf] = useState('');
  const { ini, fim, label } = faixa(periodo);

  const fat = useMemo(
    () => calcularFaturamento(estado, ini, fim),
    [estado, ini, fim]
  );
  const saidas = useMemo(
    () => somarDespesas(estado.despesas, ini, fim),
    [estado.despesas, ini, fim]
  );

  const maxMetodo = Math.max(1, ...Object.values(fat.porMetodo));
  const maxBrb = Math.max(1, ...fat.porBarbeiro.map((b) => b.bruto), 1);

  const comparativo = useMemo(() => {
    const dias =
      Math.max(
        1,
        Math.round(
          (Date.parse(`${fim}T12:00:00`) - Date.parse(`${ini}T12:00:00`)) / 86400000
        ) + 1
      );
    const iniAnt = new Date(Date.parse(`${ini}T12:00:00`));
    iniAnt.setDate(iniAnt.getDate() - dias);
    const fimAnt = new Date(Date.parse(`${ini}T12:00:00`));
    fimAnt.setDate(fimAnt.getDate() - 1);
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const ant = calcularFaturamento(estado, iso(iniAnt), iso(fimAnt));
    const delta = fat.bruto - ant.bruto;
    const pct = ant.bruto > 0 ? (delta / ant.bruto) * 100 : fat.bruto > 0 ? 100 : 0;
    return { brutoAnterior: ant.bruto, delta, pct };
  }, [estado, ini, fim, fat.bruto]);

  const baixarPdfMes = () => {
    setAvisoPdf('');
    const r = baixarResumoMensalPdf(estado, new Date());
    if (!r.ok) {
      setAvisoPdf(r.erro);
      return;
    }
    setAvisoPdf(`PDF salvo: ${r.nomeArquivo}`);
  };

  return (
    <div className="space-y-4" data-tour="fin-resumo">
      <div className="flex flex-wrap gap-2">
        {(['dia', 'semana', 'mes'] as Periodo[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriodo(p)}
            className={`rounded-xl border-2 px-3 py-2 text-xs font-bold uppercase shadow-sm ${
              periodo === p
                ? 'border-amber-900 bg-amber-600 text-white'
                : 'border-neutral-600 bg-white text-neutral-900 hover:bg-amber-50'
            }`}
          >
            {p === 'dia' ? 'Dia' : p === 'semana' ? 'Semana' : 'Mês'}
          </button>
        ))}
        <button
          type="button"
          onClick={baixarPdfMes}
          className="inline-flex items-center gap-1.5 rounded-xl border-2 border-neutral-800 bg-white px-3 py-2 text-xs font-bold text-neutral-950 shadow-sm hover:bg-amber-50"
          title="Baixar PDF do resumo deste mês (backup no celular)"
        >
          <FileDown className="h-3.5 w-3.5" />
          Baixar PDF do mês
        </button>
        <span className="self-center text-xs text-neutral-500">
          {label}: {ini} → {fim}
        </span>
      </div>
      {avisoPdf && (
        <p className="rounded-xl border border-neutral-400 bg-neutral-50 px-3 py-2 text-xs font-medium text-neutral-800">
          {avisoPdf}
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { t: 'Faturamento bruto', v: fmtBrl(fat.bruto) },
          { t: 'Comissões', v: fmtBrl(fat.comissoes) },
          { t: 'Casa (líquido parcial)', v: fmtBrl(fat.casa) },
          { t: 'Atendimentos', v: String(fat.qtdAtendimentos) },
        ].map((c) => (
          <div key={c.t} className="bb-card">
            <p className="text-[10px] font-bold uppercase tracking-wide text-neutral-500">
              {c.t}
            </p>
            <p className="mt-1 text-xl font-bold text-neutral-900">{c.v}</p>
          </div>
        ))}
      </div>

      <div className="bb-card">
        <p className="text-sm font-semibold text-neutral-800">Comparativo vs período anterior</p>
        <p className="mt-1 text-sm text-neutral-600">
          Anterior: <strong>{fmtBrl(comparativo.brutoAnterior)}</strong> · Diferença:{' '}
          <strong className={comparativo.delta >= 0 ? 'text-emerald-700' : 'text-red-700'}>
            {fmtBrl(comparativo.delta)} ({comparativo.pct.toFixed(1)}%)
          </strong>
        </p>
        <p className="mt-2 text-xs text-neutral-500">
          Despesas no período: fixas {fmtBrl(saidas.fixo)} + variáveis {fmtBrl(saidas.variavel)} ={' '}
          {fmtBrl(saidas.total)} · Resultado aproximado casa − despesas:{' '}
          <strong>{fmtBrl(fat.casa - saidas.total)}</strong>
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="bb-card">
          <h3 className="mb-3 text-sm font-bold uppercase text-neutral-800">
            Por método de pagamento
          </h3>
          <ul className="space-y-2">
            {(Object.keys(fat.porMetodo) as MetodoPagamento[]).map((m) => {
              const v = fat.porMetodo[m];
              const pct = (v / maxMetodo) * 100;
              return (
                <li key={m}>
                  <div className="mb-0.5 flex justify-between text-xs">
                    <span>{metodoLabel(m)}</span>
                    <span className="font-semibold">{fmtBrl(v)}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-neutral-100">
                    <div
                      className="h-full rounded-full bg-amber-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="bb-card">
          <h3 className="mb-3 text-sm font-bold uppercase text-neutral-800">
            Ganhos por barbeiro
          </h3>
          {fat.porBarbeiro.length === 0 ? (
            <p className="text-sm text-neutral-500">Sem atendimentos concluídos no período.</p>
          ) : (
            <ul className="space-y-2">
              {fat.porBarbeiro.map((b) => (
                <li key={b.barbeiroId}>
                  <div className="mb-0.5 flex justify-between text-xs">
                    <span className="font-semibold">{b.nome}</span>
                    <span>
                      Bruto {fmtBrl(b.bruto)} · Comissão {fmtBrl(b.comissao)}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-neutral-100">
                    <div
                      className="h-full rounded-full bg-neutral-800"
                      style={{ width: `${(b.bruto / maxBrb) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
